import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, X, AlertCircle } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { googlePickerService, GoogleDriveFile } from '../services/googlePicker';

interface GoogleDrivePickerProps {
  onFileSelect: (files: GoogleDriveFile[]) => void;
  onClose: () => void;
  isOpen: boolean;
}

const GoogleDrivePicker: React.FC<GoogleDrivePickerProps> = ({
  onFileSelect,
  onClose,
  isOpen,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    initializeGooglePicker();
  }, []);

  const initializeGooglePicker = async () => {
    try {
      // You'll need to replace these with your actual Google API credentials
      const config = {
        clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
        scope: 'https://www.googleapis.com/auth/drive.readonly',
      };

      if (!config.clientId || !config.apiKey) {
        setError('Google API credentials not configured. Please check your environment variables.');
        return;
      }

      console.log('Initializing Google Picker with config:', {
        clientId: config.clientId.substring(0, 20) + '...',
        apiKey: config.apiKey.substring(0, 20) + '...',
        scope: config.scope
      });

      await googlePickerService.initialize(config);
      setIsInitialized(true);
      console.log('Google Picker initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Picker:', error);
      setError('Failed to initialize Google Drive integration. Please refresh the page and try again.');
    }
  };

  const handleOpenPicker = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await googlePickerService.showPicker(
        (files) => {
          onFileSelect(files);
          onClose();
        },
        () => {
          // User cancelled
          onClose();
        }
      );
    } catch (error) {
      console.error('Failed to open Google Picker:', error);
      setError('Failed to open Google Drive. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="glass-effect rounded-3xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Google Drive</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {!isInitialized ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-satsuma-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Initializing Google Drive...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FolderOpen size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Select Files from Google Drive
                </h3>
                <p className="text-gray-600 text-sm mb-6">
                  Choose PDFs, documents, or spreadsheets from your Google Drive to attach to this opportunity.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Supported File Types:</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• PDF documents</li>
                  <li>• Google Docs</li>
                  <li>• Google Sheets</li>
                  <li>• Microsoft Word documents</li>
                  <li>• Microsoft Excel spreadsheets</li>
                </ul>
              </div>

              <button
                onClick={handleOpenPicker}
                disabled={isLoading}
                className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Opening Drive...</span>
                  </>
                ) : (
                  <>
                    <FolderOpen size={20} />
                    <span>Open Google Drive</span>
                  </>
                )}
              </button>

              <div className="text-center">
                <p className="text-xs text-gray-500">
                  You'll be prompted to sign in to your Google account if you haven't already.
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GoogleDrivePicker;
